import { updateEmailSetting } from "@/api/services/admin/system-setting";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { settingSchema } from "@/features/system-settings/schema/settingSchema";
import { settingFormInitValues, TSettingForm } from "@/features/system-settings/types/settingForm";
import { convertEmailSettingFormToUpdateReq } from "@/features/system-settings/utils/converts";
import { useToast } from "@/hooks/useToast";
import { text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useForm } from "react-hook-form";

type TSettingFormProps = {
  readonly?: boolean;
  previousData?: Partial<TSettingForm>;
};

export const SettingForm: FC<TSettingFormProps> = ({ readonly = false, previousData }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const methods = useForm<TSettingForm>({
    defaultValues: { ...settingFormInitValues, ...previousData },
    resolver: yupResolver(settingSchema),
    mode: "onBlur",
  });

  const { control } = methods;

  const onSubmit = async (data: TSettingForm) => {
    try {
      const payload = convertEmailSettingFormToUpdateReq(data);
      const rs = await updateEmailSetting(payload);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SYSTEM_SETTING.FETCH_SYSTEM_DETAIL],
      });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Form methods={methods} schema={settingSchema} onSubmit={onSubmit}>
      <InnerBox padding="24px" bordered sx={{ borderRadius: "0 0 8px 8px" }}>
        <Stack gap={1}>
          <Typography variant="sub16Semi" color={text.primary}>
            通知元メールアドレスの設定
          </Typography>
          <Typography variant="body14" color={text.secondary}>
            いろはなシステムから送信されるメールの送り元として使用されます。返信不可として扱われます。
          </Typography>
          <Grid container spacing={3} maxWidth={814} mt={1}>
            <Grid item xs={6}>
              <FormField control={control} name="irohanaSystemNotificationEmail">
                <Input readonly={readonly} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="clientSystemNotificationEmail">
                <Input readonly={readonly} />
              </FormField>
            </Grid>
          </Grid>
        </Stack>
      </InnerBox>

      {!readonly && (
        <StickyFooter mt={4} justifyContent="flex-end">
          <Button size="sm" type="submit">
            設定
          </Button>
        </StickyFooter>
      )}
    </Form>
  );
};
